// effectiveZoomMode — pure helper som beräknar effektiva zoom-options
// från (1) tier-baserad PlanLimits och (2) plattformskontext (mobile vs
// desktop) + valfri viewport+content-bounds-info för dynamic mobile-fit.
//
// Beslutsregler (Fas 2e):
// - Mobile (oavsett tier): tvångas till 'static' eftersom dynamic-zoom
//   bara skalar block-content medan positions stannar i world-coords —
//   det hjälper inte på små skärmar där hela flödet ska få plats.
// - Mobile zoomMin: tier-min ELLER ratio-baserad floor (canvas-bounds /
//   viewport-storlek). Det lägre värdet vinner. Floor:n beräknas så
//   att fit-to-screen alltid kan visa allt content; om viewport är
//   större än content blir floor=tier-min (viewport-fit-ratio > tier-min
//   är trivialt).
// - Desktop: använd PlanLimits direkt utan modifiering.
//
// Returvärdet matchar { mode, min, max }-shape som useCanvasScale +
// useDynamicScale konsumerar.

import type { PlanLimits } from '@/lib/planTiers';

export interface EffectiveZoomOptions {
  /** 'static' | 'dynamic' — vilket system ska aktiveras. */
  mode: 'static' | 'dynamic';
  /** Min-scale i [0, 1]. Mobile kan vara lägre än tier-min för fit-att-passa. */
  min: number;
  /** Max-scale, vanligen 1.0. */
  max: number;
}

export interface ResolveZoomArgs {
  limits: PlanLimits;
  isMobile: boolean;
  /** Viewport-dim (canvas.clientWidth/Height). Optional — om saknas
   *  används bara tier-min utan viewport-fit-ratio. */
  viewport?: { width: number; height: number };
  /** Content-bounds (största extrempunkter). Behövs för fit-floor-räkning. */
  content?: { width: number; height: number };
}

/**
 * Resolve effective zoom-config baserat på tier + plattform + viewport-
 * fit-ratio.
 *
 * @example
 *   const eff = resolveEffectiveZoom({ limits, isMobile: true, viewport, content });
 *   // mobile + Pro-tier (dynamic 0.7-1.0) → eff.mode='static', eff.min=min(0.7, viewport-fit-ratio)
 */
export function resolveEffectiveZoom(args: ResolveZoomArgs): EffectiveZoomOptions {
  const { limits, isMobile, viewport, content } = args;
  const tierMin = limits.zoomMin;
  const tierMax = limits.zoomMax;

  if (!isMobile) {
    return { mode: limits.zoomMode, min: tierMin, max: tierMax };
  }

  // Mobile: tvångs-static. Floor justeras nedåt om content är större än
  // viewport så fit-to-screen alltid kan visa allt. Om content ryms ändå
  // är fit-ratio > tier-min och floor:n stannar vid tier-min.
  let floor = tierMin;
  if (viewport && content && content.width > 0 && content.height > 0) {
    const fitRatio = Math.min(
      viewport.width / content.width,
      viewport.height / content.height,
    );
    floor = Math.min(tierMin, fitRatio);
  }

  return {
    mode: 'static',
    min: floor,
    max: tierMax,
  };
}
